import { Avatar, Button, Divider, List, Skeleton } from 'antd';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { useEffect, useMemo, useRef, useState } from 'react';
import AC from '../components/AccessControl';
import cmsFactory from '../components/cmsFactory';
import ErrorMessage from '../components/ErrorMessage';
import createComponent from '../components/contentDisplay/dynamic';
import ReportSummaries from '../components/landing/ReportSummaries';
import ToolkitsSearch from '../components/navigation/ToolkitsSearch';
import AppCard from '../components/panels/AppCard';
import HomePageTour from '../components/tour/HomePage';
import { useCurricula, useGrades } from '../dataSource/ui';
import htmlStringToReactNodes from '../helpers/htmlStringToReactNodes';
import { ReportsAccessProvider } from '../lib/ReportsAccessContext';
import styles from './index.module.scss';
const HomeLayout = ({
  mainBanner,
  mainBottom,
  mainCenter,
  asideBottom,
  asideTop
}) => <article className={styles['home-grid-container']} data-sentry-component="HomeLayout" data-sentry-source-file="index.jsx">
    <section className={styles['main-banner-wrapper']} children={mainBanner} />
    <section id="toolkits-section" className={styles['main-center-wrapper']} children={mainCenter} />
    <section id="curriculum-section" className={styles['main-bottom-wrapper']} children={mainBottom} />
    <section id="reports-section" className={styles['aside-top-wrapper']} children={asideTop} />
    <section className={styles['aside-bottom-wrapper']} children={asideBottom} />
  </article>;
const Carousel = ({
  items
}) => {
  const [current, goTo] = useState(0);
  const intervalRef = useRef(null);
  const [isPaused, setIsPaused] = useState(false);
  useEffect(() => {
    if (!isPaused) {
      intervalRef.current = setInterval(() => {
        goTo(prev => (prev + 1) % items.length);
      }, 5000);
    }
    return () => clearInterval(intervalRef.current);
  }, [items, isPaused, goTo]);
  return <div className={styles['homepage-carousel']} data-sentry-component="Carousel" data-sentry-source-file="index.jsx">
      <div className={styles['homepage-carousel-slider']} style={{
      transform: `translateX(-${current * 100}%)`
    }} children={items} onMouseEnter={() => setIsPaused(true)} onMouseLeave={() => setIsPaused(false)} />
      {items.length > 1 && <div>
          <div className={styles['homepage-carousel-buttons']}>
            {' '}
            <Button size="large" type="ghost" icon={<LeftOutlined style={{
          fontSize: '24px',
          color: 'rgba(255, 255, 255, 1)'
        }} />} onClick={() => goTo((current - 1 + items.length) % items.length)} className={styles['btn-left']} />
            <Button size="large" type="ghost" onClick={() => goTo((current + 1) % items.length)} className={styles['btn-right']} icon={<RightOutlined style={{
          fontSize: '24px',
          color: 'rgba(255, 255, 255, 1)'
        }} />} />
          </div>

          <div className={styles['homepage-carousel-dots']}>
            {items.map((_, i) => <button className={i === current ? styles['current'] : undefined} key={i} onClick={() => goTo(i)} />)}
          </div>
        </div>}
    </div>;
};
const AppsPanel = () => {
  const {
    isPending: isCurriculaPending,
    isError,
    data
  } = useCurricula();
  const rules = useMemo(() => isCurriculaPending || isError ? [] : data.map(({
    id
  }) => ({
    action: 'curriculum',
    object: 'visibility',
    args: [{
      id
    }]
  })), [isCurriculaPending, isError, data]);
  const {
    isPending: isACPending,
    accessible
  } = AC.useQueries(rules);
  const isPending = isCurriculaPending || isACPending;
  if (isPending) {
    return <Skeleton />;
  }
  if (isError) {
    return <ErrorMessage message="There was an error during loading app data. Reload the page to try again." />;
  }
  const curriculaAvailable = data.filter((_, i) => accessible[i]);
  const courses = curriculaAvailable.length === 0 ? <i children="No available courses" /> : curriculaAvailable.map(app => <AppCard app={app} key={app.id} />);
  return <>
      <h2 children="Student Courses" />
      <div className={styles['app-list']} children={courses} />
    </>;
};
const LandingPage = ({
  data: {
    carousel,
    navigation,
    blogLinks
  }
}) => {
  return <>
      <HomeLayout mainBanner={<Carousel items={carousel.map(props => ({
      ...props,
      ...Object.fromEntries(Object.keys(props).filter(k => k.endsWith('HTML')).map(k => [k.replace(/HTML$/, ''), htmlStringToReactNodes(props[k])]))
    })).map(createComponent)} />} mainCenter={<>
            <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
              <h2 children="Teacher Toolkits" />
              <span id="toolkits-search" style={{
          width: 400,
          textAlign: 'right'
        }}>
                <ToolkitsSearch placeholder="Search Toolkits..." size="large" />
              </span>
            </div>
            {navigation.map(createComponent).flatMap((c, i) => [c, <Divider key={`divider-${i}`} />]).slice(0, -1)}
          </>} mainBottom={<AppsPanel />} asideTop={<ReportsAccessProvider children={<ReportSummaries />} />} asideBottom={<>
            <h2 className="title" children="Latest from our Blogs" />
            <List itemLayout="horizontal" dataSource={blogLinks} renderItem={({
        iconURL,
        externalURL,
        title,
        name
      }) => <List.Item>
                  <List.Item.Meta avatar={<Avatar src={iconURL} />} title={<a href={externalURL} target="_blank" children={title || name} />} />
                </List.Item>} />
          </>} data-sentry-element="HomeLayout" data-sentry-source-file="index.jsx" />
      <HomePageTour data-sentry-element="HomePageTour" data-sentry-source-file="index.jsx" />
    </>;
};
export const getStaticProps = context => ({
  props: {
    dataURL: '/content/singletons/homePage.json'
  }
});
export default cmsFactory(LandingPage);